import { EventMap, EventName } from './events'

type Handler<T extends EventName> = (payload: EventMap[T]) => void | Promise<void>
type WildcardHandler = (event: EventName, payload: EventMap[EventName]) => void | Promise<void>

class EventBus {
    private handlers = new Map<EventName, Set<Handler<EventName>>>()
    private wildcardHandlers = new Set<WildcardHandler>()

    subscribe<T extends EventName>(event: T, handler: Handler<T>): () => void {
        if (!this.handlers.has(event)) {
            this.handlers.set(event, new Set())
        }

        const handlersForEvent = this.handlers.get(event) as Set<Handler<T>>
        handlersForEvent.add(handler)

        return () => {
            handlersForEvent.delete(handler)
        }
    }

    subscribeAll(handler: WildcardHandler): () => void {
        this.wildcardHandlers.add(handler)

        return () => {
            this.wildcardHandlers.delete(handler)
        }
    }

    publish<T extends EventName>(event: T, payload: EventMap[T]): void {
        this.runHandlers(event, payload)
    }

    async publishAsync<T extends EventName>(event: T, payload: EventMap[T]): Promise<void> {
        const results = this.runHandlers(event, payload)

        await Promise.allSettled(results)
    }

    private runHandlers<T extends EventName>(event: T, payload: EventMap[T]): Promise<void>[] {
        const pending: Promise<void>[] = []

        const handlersForEvent = this.handlers.get(event)

        handlersForEvent?.forEach((handler) => {
            pending.push(this.runHandlerSafely(event, () => handler(payload)))
        })

        this.wildcardHandlers.forEach((handler) => {
            pending.push(this.runHandlerSafely(event, () => handler(event, payload)))
        })

        return pending
    }

    private async runHandlerSafely(event: EventName, run: () => void | Promise<void>): Promise<void> {
        try {
            await run()
        } catch (error) {
            this.logError(event, error)
        }
    }

    private logError(event: EventName, error: unknown): void {
        // eslint-disable-next-line no-console
        console.error(`[event-bus] handler for "${event}" failed:`, error)
    }
}

export const eventBus = new EventBus()
